import PageBase from '@glittr/frontend-core/src/core/v2/app/pageBase';
import DataSource from '@glittr/frontend-core/src/core/v2/data/data-source';
import Vue from 'vue';
import DeepPartial from '@glittr/frontend-core/src/core/v2/utility-types/deep-partial';
import Int64LookupModel from '@glittr/frontend-core/src/services/v2/model/int64-lookup-model';
import User from '@glittr/frontend-core/src/plugins/auth/user';
import personalDetailsVueSchema from './edit.vue.schema';
import UploadDocumentFileRequestModel from '../../../../../services/v2/model/upload-document-file-request-model';
import PutCompanyRequestModel from '../../../../../services/v2/model/put-company-request-model';
import PutCompanyRequestDTO from '../../../../../services/v2/_generated/dto/put-company-request-dto';
import GetFileByExternalIdRequestModel from '../../../../../services/v2/model/get-file-by-external-id-request-model';
import LocationModel from '../../../../../services/v2/model/location-model';
import LookupRequestModel from '../../../../../services/v2/model/lookup-request-model';
import GuidEventLookupModel from '../../../../../services/v2/model/guid-event-lookup-model';

export default class EditCompaniesPage extends PageBase {
  public get company():PutCompanyRequestModel {
    if (this.companiesDataSource?.data?.data) {
      return this.companiesDataSource?.data?.data;
    }
    const newModel = new PutCompanyRequestModel();
    newModel.companyLocation = new LocationModel({ latitude: 47.1177271111, longitude: 9.1492561111 });
    return newModel;
  }

  public error?: Error | string = undefined;

  public companyId: number | undefined = undefined;

  public adminMode: boolean = false;

  public logoDocument: undefined | File[];

  public professionItems: Int64LookupModel[] | undefined = undefined;

  public eventItems: GuidEventLookupModel[] | undefined;

  public user: User | undefined;

  public genderOptions() {
    return [
      { id: 0, caption: Vue.$t('core.address.salutationEnum3') },
      { id: 1, caption: Vue.$t('core.address.salutationEnum1') },
      { id: 2, caption: Vue.$t('core.address.salutationEnum2') },
    ];
  }

  public professionsDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.tdg_Professions.lookupAllProfessions,
  });

  private companiesDataSourceAdmin = new DataSource({
    selectCommand: Vue.$service.v2.api.tdg_Companies.getCompany,
    updateCommand: Vue.$service.v2.api.tdg_Companies.putCompany,
  });

  private companiesDataSourceMy = new DataSource({
    selectCommand: Vue.$service.v2.api.tdg_Companies.getCompanyForUser,
    updateCommand: Vue.$service.v2.api.tdg_Companies.putCompany,
  });

  public eventsDataSource = new DataSource({
    selectCommand: () => {
      const req = new LookupRequestModel();
      return Vue.$service.v2.api.tdg_Events.eventLookup(req);
    },
  });

  private companiesDataSource?: typeof this.companiesDataSourceAdmin | typeof this.companiesDataSourceMy = undefined;

  async initialize(): Promise<void> {
    if (this.adminMode && !Number.isNaN(this.companyId)) {
      this.companiesDataSourceAdmin.filter.id = this.companyId;
      this.companiesDataSource = this.companiesDataSourceAdmin;
    } else {
      this.companiesDataSource = this.companiesDataSourceMy;
    }
    await this.eventsDataSource.select();
    await this.loadEventLookup();
    await this.loadProfessionLookup();
    this.user = await Vue.$auth.getUser();
    if (this.user && this.user.id != null && this.adminMode !== true) {
      this.companiesDataSourceMy.filter.userId = this.user?.id;
      await this.companiesDataSource.select();
    }
    if (this.company.logoDocumentExternalId) {
      const requestDocument = new GetFileByExternalIdRequestModel();
      requestDocument.externalId = this.company.logoDocumentExternalId;
      const document = await Vue.$service.v2.api.tdg_Documents.getTdgDocumentByExternalId(requestDocument);
      try {
        const fileContent = await Vue.$service.v2.api.tdg_Documents.downloadDocumentFile(requestDocument);
        const file = new File([fileContent.data], document.data.originalFileName || 'unknown Filename', { type: document.data.contentType });
        Vue.set(this, 'logoDocument', [file]);
      } catch (error) {
        Vue.set(this, 'logoDocument', []);
      }
    }
    if (this.company.companyLocation === undefined) {
      this.company.companyLocation = new LocationModel();
    }
  }

  public editCompanies = async () => {
    const valid = this.company.validateWithSchema(personalDetailsVueSchema);
    if (valid) {
      const updateModel = new PutCompanyRequestModel(this.company as DeepPartial<PutCompanyRequestDTO>);
      const logoFiles = this.logoDocument ?? [];
      if (logoFiles.length > 0) {
        const request = new UploadDocumentFileRequestModel();
        request.uploadFile = this.logoDocument?.[0];
        const documentId = await Vue.$service.v2.api.tdg_Documents.uploadDocumentFile(request);
        updateModel.logoDocumentExternalId = documentId.data;
      } else {
        updateModel.logoDocumentExternalId = undefined;
      }

      // Workaround: #16708 Datetime-Validierung schlägt fehl
      updateModel.modified = undefined;
      updateModel.created = undefined;

      if (this.user) {
        updateModel.companyUserId = this.user.id ?? undefined;
      }
      try {
        await this.companiesDataSource!.update(updateModel);
        Vue.$router.back();
      } catch (error: any) {
        Vue.$log.error(error);
        this.error = error;
      }
    }
  };

  async loadProfessionLookup() {
    try {
      await this.professionsDataSource.select();
      if (this.professionsDataSource.data) {
        this.professionItems = this.professionsDataSource.data.items.map((i) => i.data);
      }
    } catch (error) {
      Vue.$log.error(`Failed to load organisations: ${error}`);
    }
  }

  async loadEventLookup() {
    if (this.eventsDataSource.data) {
      const res = this.eventsDataSource.data;

      this.eventItems = res.items.map((i) => {
        const model = new GuidEventLookupModel();
        model.id = i.data.id;
        model.caption = i.data.caption;
        return model;
      });

      if (this.eventItems.length > 0) {
        const lastItem = this.eventItems[this.eventItems.length - 1];
        this.eventsDataSource.filter.eventIdentifier = lastItem.id;
      }
    }
  }
}
